import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, query, onSnapshot, doc, setDoc, serverTimestamp, setLogLevel, deleteDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage"; 
import { PlusCircle, ListChecks, Briefcase, User, Building, AlertCircle, Info, FileText, Tag, ArrowLeftCircle, HardHat, Power, Trash2, Edit3, XCircle, RotateCcw, UploadCloud, Paperclip, RefreshCw, Eye, Brain, ChevronDown } from 'lucide-react';
import {
  calculateIndividualEquipmentConsumption,
  aggregateConsumptionByCategories,
  determinePredominantUse
} from './studyLogic';
import html2pdf from 'html2pdf.js';

// --- FormInput component ---
function FormInput({ label, name, value, onChange, placeholder, required }) {
  return (
    <div className="mb-2">
      <label className="block text-slate-300 mb-1 font-medium">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full p-2 rounded bg-slate-600 text-slate-100 border border-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
      />
    </div>
  );
}

// --- FormSelect component ---
function FormSelect({ label, name, value, onChange, options, required }) {
  return (
    <div className="mb-2">
      <label className="block text-slate-300 mb-1 font-medium">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full p-2 rounded bg-slate-600 text-slate-100 border border-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
      >
        {options.map(opt =>
          typeof opt === 'string'
            ? <option key={opt} value={opt}>{opt}</option>
            : <option key={opt.id || opt.value} value={opt.id || opt.value}>{opt.label || opt}</option>
        )}
      </select>
    </div>
  );
}

// --- Firebase Configuration and Initialization ---
const canvasAppId = (typeof window !== 'undefined' && window.__app_id) ? window.__app_id : 'default-predominant-use-app';

const firebaseConfig = {
  apiKey: "AIzaSyAmuViaPFsi5chx2VoRN_GlG3gLaTIaaiE", // This is your Firebase Web API Key, different from Gemini API Key
  authDomain: "legal-assist-458705.firebaseapp.com",
  projectId: "legal-assist-458705",
  storageBucket: "legal-assist-458705.firebasestorage.app", 
  messagingSenderId: "1065399015386",
  appId: "1:1065399015386:web:1ad3b826f669d031ff52e3",
  measurementId: "G-2E2ZFTJ75J" 
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);
setLogLevel('debug');

// --- Constants ---
const PROJECT_STATUSES = ["Planning", "Data Collection", "Analysis", "Review", "Completed", "Archived"];
const FACILITY_TYPES = ["Manufacturing", "Agricultural", "Commercial", "Industrial", "Data Center", "Other"];
const EQUIPMENT_TYPES = ["Motor", "HVAC Unit", "Lighting Fixture", "Process Machine", "Pump", "Compressor", "Computer/Server", "Office Equipment", "Other"];
const EQUIPMENT_PHASES = ["N/A", "1-Phase", "3-Phase"];
const DOCUMENT_TYPES = ["Utility Bill", "Equipment Specification", "Nameplate Photo", "Facility Blueprint", "Tax Document", "Other"];

// --- Add Usage Categories ---
const USAGE_CATEGORIES = [
  { id: 'MANUFACTURING_PROCESS', label: 'Manufacturing Process (Exempt)', isExempt: true },
  { id: 'PRODUCTION_SUPPORT', label: 'Production Support (Potentially Exempt)', isExempt: true },
  { id: 'GENERAL_LIGHTING', label: 'General Lighting (Non-Exempt)', isExempt: false },
  { id: 'OFFICE_HVAC', label: 'Office HVAC (Non-Exempt)', isExempt: false },
  { id: 'NON_PROCESS_OTHER', label: 'Other Non-Process (Non-Exempt)', isExempt: false },
];

// --- Define processable file types for Gemini API ---
const PROCESSABLE_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/bmp",
  "image/gif",
  "image/webp"
];
const PROCESSABLE_TEXT_TYPES = [
  "text/plain",
  "text/csv",
  "application/pdf"
];

const initialEquipmentFormState = {
  equipmentName: '', equipmentType: EQUIPMENT_TYPES[0], powerRating: '', manufacturer: '',
  modelNumber: '', serialNumber: '', voltage: '', phase: EQUIPMENT_PHASES[0],
  locationInFacility: '', estimatedOperatingHours: '', loadFactor: '',
  usageCategory: USAGE_CATEGORIES[0].id, // <-- Add usageCategory to initial state
};


// --- Main Application Component ---
function App() {
  // --- Auth State ---
  const [currentUser, setCurrentUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  
  // --- Project State ---
  const [projects, setProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectError, setProjectError] = useState(null);
  const [projectForm, setProjectForm] = useState({
    projectName: '', clientName: '', facilityName: '', studyPeriod: '',
    facilityType: FACILITY_TYPES[0], utilityProviders: '', projectStatus: PROJECT_STATUSES[0]
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const [editingProjectStatusId, setEditingProjectStatusId] = useState(null);

  // --- Equipment State ---
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [equipmentList, setEquipmentList] = useState([]);
  const [isLoadingEquipment, setIsLoadingEquipment] = useState(false);
  const [equipmentError, setEquipmentError] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState(initialEquipmentFormState);
  const [isSubmittingEquipment, setIsSubmittingEquipment] = useState(false);
  const [editingEquipmentId, setEditingEquipmentId] = useState(null);

  // --- Document/Data Ingestion State ---
  const [projectDocuments, setProjectDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [documentError, setDocumentError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState(DOCUMENT_TYPES[0]);
  const [documentName, setDocumentName] = useState('');
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingDocId, setProcessingDocId] = useState(null); 


  // --- Modal State for Deletion ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); 


  // --- Authentication Effect ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => { 
      if (user) {
        setCurrentUser(user); 
        setUserId(user.uid);
      } else {
        try {
          if (typeof window !== 'undefined' && typeof window.__initial_auth_token !== 'undefined' && window.__initial_auth_token) {
             console.log("Attempting to sign in with custom token...");
            await signInWithCustomToken(auth, window.__initial_auth_token);
          } else {
            console.log("Attempting to sign in anonymously for local development...");
            await signInAnonymously(auth);
          }
        } catch (err) { 
          console.error("Error during sign-in:", err);
          setProjectError("Authentication failed: " + err.message);
          setCurrentUser(null); 
          setUserId(null);
        }
      }
      setIsAuthReady(true); 
    });
    return () => unsubscribe(); 
  }, []); 

  // --- Firestore Project Data Fetching ---
  useEffect(() => {
    if (!isAuthReady || !userId) {
      if(isAuthReady && !userId) setIsLoadingProjects(false); 
      return;
    }
    setIsLoadingProjects(true); setProjectError(null);
    const projectsCollectionPath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects`;
    const q = query(collection(db, projectsCollectionPath));
    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        const projectsData = [];
        querySnapshot.forEach((doc) => projectsData.push({ id: doc.id, ...doc.data() }));
        projectsData.sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0));
        setProjects(projectsData);
        setIsLoadingProjects(false);
      }, 
      (err) => {
        console.error("Error fetching projects:", err);
        setProjectError("Failed to load projects: " + err.message);
        setIsLoadingProjects(false);
      }
    );
    return () => unsubscribe(); 
  }, [isAuthReady, userId]);

  // --- Firestore Equipment Data Fetching ---
  useEffect(() => {
    if (!selectedProjectId || !userId) {
      setEquipmentList([]); 
      setSelectedProject(null);
      setProjectDocuments([]);
      return;
    }
    setIsLoadingEquipment(true); setEquipmentError(null);
    const currentProject = projects.find(p => p.id === selectedProjectId);
    setSelectedProject(currentProject);

    const equipmentCollectionPath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/equipment`;
    const qEquip = query(collection(db, equipmentCollectionPath));
    const unsubscribeEquip = onSnapshot(qEquip,
      (querySnapshot) => {
        const equipData = [];
        querySnapshot.forEach((doc) => equipData.push({ id: doc.id, ...doc.data() }));
        equipData.sort((a, b) => (a.equipmentName?.toLowerCase() || "").localeCompare(b.equipmentName?.toLowerCase() || ""));
        setEquipmentList(equipData);
        setIsLoadingEquipment(false);
      },
      (err) => {
        console.error(`Error fetching equipment for project ${selectedProjectId}:`, err);
        setEquipmentError("Failed to load equipment: " + err.message);
        setIsLoadingEquipment(false);
      }
    );
    return () => unsubscribeEquip();
  }, [selectedProjectId, userId, projects]);

  // --- Firestore Project Documents Data Fetching ---
  useEffect(() => {
    if (!selectedProjectId || !userId) {
      setProjectDocuments([]);
      return;
    }
    setIsLoadingDocuments(true); setDocumentError(null);
    const documentsCollectionPath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/documents`;
    const qDocs = query(collection(db, documentsCollectionPath));
    const unsubscribeDocs = onSnapshot(qDocs,
      (querySnapshot) => {
        const docsData = [];
        querySnapshot.forEach((doc) => docsData.push({ id: doc.id, ...doc.data() }));
        docsData.sort((a, b) => (b.uploadedAt?.toDate() || 0) - (a.uploadedAt?.toDate() || 0));
        setProjectDocuments(docsData);
        setIsLoadingDocuments(false);
      },
      (err) => {
        console.error(`Error fetching documents for project ${selectedProjectId}:`, err);
        setDocumentError("Failed to load documents: " + err.message);
        setIsLoadingDocuments(false);
      }
    );
    return () => unsubscribeDocs();
  }, [selectedProjectId, userId]);


  // --- Project Form Handler ---
  // (Removed duplicate declaration of handleProjectFormChange to fix redeclaration error)

  // Duplicate handleProjectStatusChange removed to fix redeclaration error.
  
  // --- Equipment Form Handler ---
  // (Removed duplicate declaration of handleEquipmentFormChange to fix redeclaration error)

  // (handleEquipmentSubmit duplicate removed here)

  // Duplicate handleEditEquipment removed to fix redeclaration error.


  // --- Document Upload Handler ---
  // Duplicate handleFileSelect removed to fix redeclaration error.
  
  // (Removed duplicate handleAddDocument to fix redeclaration error)

  // --- Gemini API Document Processing ---
  // (Duplicate handleProcessDocument removed to fix redeclaration error)


  // --- Deletion Handling ---
  // (Duplicate openDeleteModal removed to fix redeclaration error)


  // --- Analysis State ---
  // (Removed duplicate declaration of analysisError here)

  // --- Run Analysis Handler ---
  // (Removed duplicate declaration of handleRunAnalysis to fix redeclaration error)

  // --- Quick Category Change Handler ---
  // (Duplicate removed to fix redeclaration error)

  // --- ReportPreview Component ---
    // Helper function to estimate study duration in months from project.studyPeriod string
    function getStudyDurationMonths(project) {
      if (!project || !project.studyPeriod) return 12; // Default to 12 months if not specified
      // Try to parse "Jan 2025 - Dec 2025" or similar
      const match = project.studyPeriod.match(/([A-Za-z]+)\s*(\d{4})\s*-\s*([A-Za-z]+)\s*(\d{4})/);
      if (match) {
        const startMonth = match[1];
        const startYear = parseInt(match[2], 10);
        const endMonth = match[3];
        const endYear = parseInt(match[4], 10);
        const months = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const startIdx = months.findIndex(m => m.toLowerCase().startsWith(startMonth.toLowerCase().slice(0, 3)));
        const endIdx = months.findIndex(m => m.toLowerCase().startsWith(endMonth.toLowerCase().slice(0, 3)));
        if (startIdx !== -1 && endIdx !== -1) {
          return (endYear - startYear) * 12 + (endIdx - startIdx) + 1;
        }
      }
      return 12; // fallback
    }
  
    function ReportPreview({ project, analysisResults, equipmentList }) {
      if (!project || !analysisResults) return null;
      return (
        <div id="report-preview" style={{ background: "#fff", color: "#222", padding: 24, fontFamily: "sans-serif", maxWidth: 800, margin: "0 auto" }}>
          <h1 style={{ color: "#0369a1" }}>Predominant Use Study Report</h1>
          <h2 style={{ marginTop: 0 }}>{project.projectName}</h2>
          <p><strong>Client:</strong> {project.clientName}</p>
          <p><strong>Facility:</strong> {project.facilityName}</p>
          <p><strong>Study Period:</strong> {project.studyPeriod}</p>
          <hr style={{ margin: "16px 0" }} />
          <h3>Summary</h3>
          <p>
            <strong>Total Exempt %:</strong> {analysisResults.exemptPercentage?.toFixed(1)}%<br />
            <strong>Predominant Use Test:</strong>{" "}
            {analysisResults.isPredominantlyExempt ? "MET" : "NOT MET"}
          </p>
          <hr style={{ margin: "16px 0" }} />
          <h3>Category Breakdown</h3>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
            <thead>
              <tr>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Usage Category</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>kWh</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>% of Total</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Exempt?</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(analysisResults.details)
                .filter(([key]) => key !== 'totalKWh')
                .map(([catId, cat]) => (
                  <tr key={catId}>
                    <td style={{ border: "1px solid #ccc", padding: 6 }}>{cat.label || catId}</td>
                    <td style={{ border: "1px solid #ccc", padding: 6 }}>{cat.kWh.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                    <td style={{ border: "1px solid #ccc", padding: 6 }}>
                      {analysisResults.details.totalKWh > 0
                        ? ((cat.kWh / analysisResults.details.totalKWh) * 100).toFixed(1) + '%'
                        : '-'}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: 6 }}>{cat.isExempt ? "Yes" : "No"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          <h3>Equipment List</h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Name/ID</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Type</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Power (kW)</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Usage Category</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Est. Hours</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Load Factor (%)</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Est. Consumption (kWh)</th>
              </tr>
            </thead>
            <tbody>
              {equipmentList.map(equip => (
                <tr key={equip.id}>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>{equip.equipmentName}</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>{equip.equipmentType}</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>{equip.powerRating}</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>
                    {USAGE_CATEGORIES.find(cat => cat.id === equip.usageCategory)?.label || equip.usageCategory}
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>{equip.estimatedOperatingHours}</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>{equip.loadFactor}</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>
                    {calculateIndividualEquipmentConsumption(
                      equip,
                      getStudyDurationMonths(project)
                    ).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 24, fontSize: 12, color: "#888" }}>
            Generated by Predominant Use AI | {new Date().toLocaleDateString()}
          </div>
        </div>
      );
    }

  // --- PDF Generation Handler ---
  const handleGenerateReport = () => {
    if (!selectedProject || !analysisResults) return;
    const element = document.getElementById('report-preview');
    if (!element) return;
    html2pdf()
      .set({
        margin: 0.5,
        filename: `${selectedProject.projectName || 'predominant-use-study'}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      })
      .from(element)
      .save();
  };

  // --- Authentication Effect ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => { 
      if (user) {
        setCurrentUser(user); 
        setUserId(user.uid);
      } else {
        try {
          if (typeof window !== 'undefined' && typeof window.__initial_auth_token !== 'undefined' && window.__initial_auth_token) {
             console.log("Attempting to sign in with custom token...");
            await signInWithCustomToken(auth, window.__initial_auth_token);
          } else {
            console.log("Attempting to sign in anonymously for local development...");
            await signInAnonymously(auth);
          }
        } catch (err) { 
          console.error("Error during sign-in:", err);
          setProjectError("Authentication failed: " + err.message);
          setCurrentUser(null); 
          setUserId(null);
        }
      }
      setIsAuthReady(true); 
    });
    return () => unsubscribe(); 
  }, []); 

  // --- Firestore Project Data Fetching ---
  useEffect(() => {
    if (!isAuthReady || !userId) {
      if(isAuthReady && !userId) setIsLoadingProjects(false); 
      return;
    }
    setIsLoadingProjects(true); setProjectError(null);
    const projectsCollectionPath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects`;
    const q = query(collection(db, projectsCollectionPath));
    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        const projectsData = [];
        querySnapshot.forEach((doc) => projectsData.push({ id: doc.id, ...doc.data() }));
        projectsData.sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0));
        setProjects(projectsData);
        setIsLoadingProjects(false);
      }, 
      (err) => {
        console.error("Error fetching projects:", err);
        setProjectError("Failed to load projects: " + err.message);
        setIsLoadingProjects(false);
      }
    );
    return () => unsubscribe(); 
  }, [isAuthReady, userId]);

  // --- Firestore Equipment Data Fetching ---
  useEffect(() => {
    if (!selectedProjectId || !userId) {
      setEquipmentList([]); 
      setSelectedProject(null);
      setProjectDocuments([]);
      return;
    }
    setIsLoadingEquipment(true); setEquipmentError(null);
    const currentProject = projects.find(p => p.id === selectedProjectId);
    setSelectedProject(currentProject);

    const equipmentCollectionPath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/equipment`;
    const qEquip = query(collection(db, equipmentCollectionPath));
    const unsubscribeEquip = onSnapshot(qEquip,
      (querySnapshot) => {
        const equipData = [];
        querySnapshot.forEach((doc) => equipData.push({ id: doc.id, ...doc.data() }));
        equipData.sort((a, b) => (a.equipmentName?.toLowerCase() || "").localeCompare(b.equipmentName?.toLowerCase() || ""));
        setEquipmentList(equipData);
        setIsLoadingEquipment(false);
      },
      (err) => {
        console.error(`Error fetching equipment for project ${selectedProjectId}:`, err);
        setEquipmentError("Failed to load equipment: " + err.message);
        setIsLoadingEquipment(false);
      }
    );
    return () => unsubscribeEquip();
  }, [selectedProjectId, userId, projects]);

  // --- Firestore Project Documents Data Fetching ---
  useEffect(() => {
    if (!selectedProjectId || !userId) {
      setProjectDocuments([]);
      return;
    }
    setIsLoadingDocuments(true); setDocumentError(null);
    const documentsCollectionPath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/documents`;
    const qDocs = query(collection(db, documentsCollectionPath));
    const unsubscribeDocs = onSnapshot(qDocs,
      (querySnapshot) => {
        const docsData = [];
        querySnapshot.forEach((doc) => docsData.push({ id: doc.id, ...doc.data() }));
        docsData.sort((a, b) => (b.uploadedAt?.toDate() || 0) - (a.uploadedAt?.toDate() || 0));
        setProjectDocuments(docsData);
        setIsLoadingDocuments(false);
      },
      (err) => {
        console.error(`Error fetching documents for project ${selectedProjectId}:`, err);
        setDocumentError("Failed to load documents: " + err.message);
        setIsLoadingDocuments(false);
      }
    );
    return () => unsubscribeDocs();
  }, [selectedProjectId, userId]);


  // --- Project Form Handler ---
  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    setProjectForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (Object.values(projectForm).some(val => typeof val === 'string' && !val.trim())) {
      setProjectError("All project fields are required."); return;
    }
    if (!userId) { setProjectError("User not authenticated."); return; }

    setIsSubmittingProject(true); setProjectError(null);
    try {
      const projectsCollectionPath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects`;
      await addDoc(collection(db, projectsCollectionPath), { ...projectForm, createdAt: serverTimestamp() });
      setProjectForm({
        projectName: '', clientName: '', facilityName: '', studyPeriod: '',
        facilityType: FACILITY_TYPES[0], utilityProviders: '', projectStatus: PROJECT_STATUSES[0]
      });
    } catch (err) {
      console.error("Error adding project:", err);
      setProjectError("Failed to add project: " + err.message);
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleProjectStatusChange = async (projectId, newStatus) => {
    if (!userId || !projectId) {
      setProjectError("User or Project ID missing for status update.");
      return;
    }
    setProjectError(null);
    const projectDocRef = doc(db, `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects`, projectId);
    try {
      await updateDoc(projectDocRef, {
        projectStatus: newStatus,
        lastStatusUpdateAt: serverTimestamp() 
      });
      console.log(`Project ${projectId} status updated to ${newStatus}`);
    } catch (err) {
      console.error("Error updating project status:", err);
      setProjectError(`Failed to update status for project ${projectId}: ${err.message}`);
    }
    setEditingProjectStatusId(null); 
  };
  
  // --- Equipment Form Handler ---
  const handleEquipmentFormChange = (e) => {
    const { name, value } = e.target;
    setEquipmentForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEquipmentSubmit = async (e) => {
    e.preventDefault();
    if (!equipmentForm.equipmentName.trim() || !equipmentForm.powerRating.trim()) {
      setEquipmentError("Equipment Name and Power Rating are required."); return;
    }
    if (!userId || !selectedProjectId) { setEquipmentError("User or Project not selected."); return; }

    setIsSubmittingEquipment(true); setEquipmentError(null);
    const equipmentData = {
      ...equipmentForm,
      powerRating: parseFloat(equipmentForm.powerRating) || 0,
      loadFactor: parseFloat(equipmentForm.loadFactor) || 0,
      usageCategory: equipmentForm.usageCategory || USAGE_CATEGORIES[0].id, // <-- Ensure usageCategory is saved
    };

    try {
      const equipmentCollectionPath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/equipment`;
      if (editingEquipmentId) {
        const equipDocRef = doc(db, equipmentCollectionPath, editingEquipmentId);
        await updateDoc(equipDocRef, { ...equipmentData, lastUpdatedAt: serverTimestamp() });
        setEditingEquipmentId(null);
      } else {
        await addDoc(collection(db, equipmentCollectionPath), { ...equipmentData, addedAt: serverTimestamp() });
      }
      setEquipmentForm(initialEquipmentFormState);
    } catch (err) {
      console.error("Error saving equipment:", err);
      setEquipmentError("Failed to save equipment: " + err.message);
    } finally {
      setIsSubmittingEquipment(false);
    }
  };

  const handleEditEquipment = (equipment) => {
    setEditingEquipmentId(equipment.id);
    setEquipmentForm({
      equipmentName: equipment.equipmentName || '', equipmentType: equipment.equipmentType || EQUIPMENT_TYPES[0],
      powerRating: equipment.powerRating || '', manufacturer: equipment.manufacturer || '',
      modelNumber: equipment.modelNumber || '', serialNumber: equipment.serialNumber || '',
      voltage: equipment.voltage || '', phase: equipment.phase || EQUIPMENT_PHASES[0],
      locationInFacility: equipment.locationInFacility || '', estimatedOperatingHours: equipment.estimatedOperatingHours || '',
      loadFactor: equipment.loadFactor || '',
      usageCategory: equipment.usageCategory || USAGE_CATEGORIES[0].id, // <-- Add usageCategory to edit
    });
    document.getElementById('equipment-form-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const cancelEditEquipment = () => {
    setEditingEquipmentId(null);
    setEquipmentForm(initialEquipmentFormState);
    setEquipmentError(null);
  };

  // --- Document Upload Handler ---
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setDocumentName(file.name); 
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!selectedFile) { setDocumentError("Please select a file to upload."); return; }
    if (!documentName.trim()) { setDocumentError("Please provide a name for the document."); return; }
    if (!userId || !selectedProjectId) { setDocumentError("User or Project not selected."); return; }

    setIsUploadingDocument(true); setUploadProgress(0); setDocumentError(null);
    
    const firestoreDocId = doc(collection(db, `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/documents`)).id;
    const storagePath = `users/${userId}/projects/${selectedProjectId}/documents/${firestoreDocId}/${selectedFile.name}`;
    
    console.log("Attempting to upload to Firebase Storage path:", storagePath); 
    
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, selectedFile);

    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Upload failed:", error);
        setDocumentError(`Upload failed: ${error.message}`);
        setIsUploadingDocument(false); setUploadProgress(0);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const docMetadata = {
            originalFileName: selectedFile.name,
            displayName: documentName,
            fileType: selectedFile.type,
            fileSize: selectedFile.size,
            documentCategory: documentType,
            uploadedAt: serverTimestamp(),
            storagePath: storagePath,
            downloadURL: downloadURL,
            processingStatus: "Uploaded", 
            extractedText: null, 
          };
          await setDoc(doc(db, `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/documents`, firestoreDocId), docMetadata);
          
          setSelectedFile(null); setDocumentName(''); setDocumentType(DOCUMENT_TYPES[0]);
          if(document.getElementById('file-input')) document.getElementById('file-input').value = null;
          setUploadProgress(0);
        } catch (err) {
          console.error("Error saving document metadata or getting download URL:", err);
          setDocumentError("Failed to save document metadata: " + err.message);
        } finally {
          setIsUploadingDocument(false);
        }
      }
    );
  };

  // --- Gemini API Document Processing ---
  const handleProcessDocument = async (documentItem) => {
    if (!documentItem || !documentItem.id || !documentItem.storagePath || !selectedProjectId || !userId) {
      setDocumentError("Invalid document data for processing.");
      return;
    }
    setProcessingDocId(documentItem.id);
    setDocumentError(null);

    // IMPORTANT: Replace with your actual Gemini API Key
    const geminiApiKey = "AIzaSyCVro3O004XWmlnSlHMANQt0VbjYlNCme4"; // <--- REPLACE THIS WITH YOUR KEY
    if (geminiApiKey === "AIzaSyCVro3O004XWmlnSlHMANQt0VbjYlNCme4" || !geminiApiKey) {
        setDocumentError("Gemini API Key is not configured in the application code.");
        await updateDoc(doc(db, `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/documents`, documentItem.id), 
            { processingStatus: "Error", extractedText: "API Key missing." });
        setProcessingDocId(null);
        return;
    }


    const docRef = doc(db, `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/documents`, documentItem.id);
    await updateDoc(docRef, { processingStatus: "Processing", extractedText: "Processing..." });

    try {
      let prompt = "";
      let fileContentForApi = null; 

      if (PROCESSABLE_IMAGE_TYPES.includes(documentItem.fileType)) {
        prompt = "Extract all text from this image. If no text is present, respond with 'No text found'.";
        const response = await fetch(documentItem.downloadURL);
        if (!response.ok) throw new Error(`Failed to fetch image for processing: ${response.statusText}`);
        const blob = await response.blob();
        fileContentForApi = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]); 
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

      } else if (PROCESSABLE_TEXT_TYPES.includes(documentItem.fileType)) {
        prompt = "Summarize the following text in a few key points:";
        const response = await fetch(documentItem.downloadURL);
        if (!response.ok) throw new Error(`Failed to fetch text file for processing: ${response.statusText}`);
        fileContentForApi = await response.text();

      } else {
        await updateDoc(docRef, { processingStatus: "Non-Processable", extractedText: "File type not supported for automated processing in this demo." });
        setProcessingDocId(null);
        return;
      }

      let chatHistory = [];
      const parts = [{ text: prompt }];
      if (PROCESSABLE_IMAGE_TYPES.includes(documentItem.fileType) && fileContentForApi) {
          parts.push({ inlineData: { mimeType: documentItem.fileType, data: fileContentForApi } });
      } else if (PROCESSABLE_TEXT_TYPES.includes(documentItem.fileType) && fileContentForApi) {
          parts[0].text = prompt + "\n\n" + fileContentForApi; 
      }
      
      chatHistory.push({ role: "user", parts: parts });
      const payload = { contents: chatHistory };
      
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${geminiApiKey}`;
      
      const geminiResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!geminiResponse.ok) {
        const errorResult = await geminiResponse.json();
        console.error("Gemini API Error:", errorResult);
        throw new Error(`Gemini API request failed: ${errorResult.error?.message || geminiResponse.statusText}`);
      }

      const result = await geminiResponse.json();
      let extractedTextOutput = "No content generated.";

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        extractedTextOutput = result.candidates[0].content.parts[0].text;
      } else {
         console.warn("Gemini API response structure unexpected or content missing", result);
         extractedTextOutput = "Could not extract text or summary from the document. Response from AI was empty or malformed."
      }
      
      await updateDoc(docRef, { processingStatus: "Processed", extractedText: extractedTextOutput });

    } catch (err) {
      console.error("Error processing document with Gemini:", err);
      await updateDoc(docRef, { processingStatus: "Error", extractedText: `Processing failed: ${err.message}` });
      setDocumentError(`Processing failed for ${documentItem.displayName}: ${err.message}`);
    } finally {
      setProcessingDocId(null);
    }
  };


  // --- Deletion Handling ---
  const openDeleteModal = (id, type, itemDetails = {}) => {
    setItemToDelete({ id, type, ...itemDetails });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete || !userId) {
      console.error("No item selected for deletion or user not authenticated.");
      closeDeleteModal(); return;
    }
    const { id, type, storagePath: docStoragePath } = itemToDelete; 
    let itemFirestorePath;

    try {
      if (type === 'project') {
        itemFirestorePath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${id}`;
        console.warn(`Deleting project ${id}. Associated sub-collections (equipment, documents) and files in Storage will NOT be automatically deleted from Firestore/Storage in this example. Manual cleanup needed.`);
        await deleteDoc(doc(db, itemFirestorePath));
        if (id === selectedProjectId) setSelectedProjectId(null);

      } else if (type === 'equipment' && selectedProjectId) {
        itemFirestorePath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/equipment/${id}`;
        await deleteDoc(doc(db, itemFirestorePath));

      } else if (type === 'document' && selectedProjectId) {
        itemFirestorePath = `artifacts/${canvasAppId}/users/${userId}/predominantUseProjects/${selectedProjectId}/documents/${id}`;
        await deleteDoc(doc(db, itemFirestorePath));
        if (docStoragePath) {
          const fileRef = ref(storage, docStoragePath);
          await deleteObject(fileRef).catch(err => console.warn("Error deleting file from storage, it might have already been deleted or path is incorrect:", err));
        }
      } else {
        console.error("Invalid item type or missing project ID for deletion.");
        closeDeleteModal(); return;
      }
    } catch (err) {
      console.error(`Error deleting ${type}:`, err);
      const errorSetter = type === 'project' ? setProjectError : type === 'equipment' ? setEquipmentError : setDocumentError;
      errorSetter(`Failed to delete ${type}: ${err.message}`);
    } finally {
      closeDeleteModal();
    }
  };

  // --- Analysis State ---
  const [analysisResults, setAnalysisResults] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  // --- Run Analysis Handler ---
  const handleRunAnalysis = useCallback(() => {
    setAnalysisLoading(true);
    setAnalysisError(null);
    try {
      const months = getStudyDurationMonths(selectedProject);
      const details = aggregateConsumptionByCategories(equipmentList, months, USAGE_CATEGORIES);
      const result = determinePredominantUse(details, 50);
      setAnalysisResults({
        details,
        exemptPercentage: result.exemptPercentage,
        isPredominantlyExempt: result.isPredominantlyExempt
      });
    } catch (err) {
      setAnalysisError("Analysis failed: " + err.message);
      setAnalysisResults(null);
    } finally {
      setAnalysisLoading(false);
    }
  }, [equipmentList, selectedProject]);
    
  // --- Render Logic ---
  if (!isAuthReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100 text-slate-700">
        <Info size={48} className="mr-4 animate-spin text-sky-600" /> <p className="text-xl">Initializing Authentication...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-slate-100 p-4 sm:p-6 md:p-8 font-sans">
      {/* --- HEADER --- */}
      <header className="mb-8 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-sky-400 tracking-tight">Predominant Use AI</h1>
        <p className="text-slate-400 mt-2 text-lg">
          {selectedProjectId && selectedProject ? `Managing: ${selectedProject.projectName}` : "Project & Client Management"}
        </p>
      </header>

      {/* --- INFO BAR --- */}
      <div className="mb-6 p-4 bg-slate-700 rounded-lg shadow-md text-xs sm:text-sm">
        <p><strong className="text-sky-400">Canvas App ID (for Firestore paths):</strong> {canvasAppId}</p>
        {userId ? <p><strong className="text-sky-400">User ID:</strong> {userId}</p> : <p className="text-amber-400">User: Not Authenticated</p>}
      </div>

      {/* --- ERROR BANNERS --- */}
      {(projectError && !selectedProjectId) && (
        <div className="mb-6 p-4 bg-red-500/20 text-red-300 border border-red-500 rounded-lg flex items-center">
          <AlertCircle size={20} className="mr-3 text-red-400" /> <span>{projectError}</span>
        </div>
      )}
      {selectedProjectId && (equipmentError || documentError) && (
        <div className="mb-6 p-4 bg-red-500/20 text-red-300 border border-red-500 rounded-lg flex items-center">
          <AlertCircle size={20} className="mr-3 text-red-400" />
          <span>
            {equipmentError && `Equipment Error: ${equipmentError}`}
            {equipmentError && documentError && <br />}
            {documentError && `Document Error: ${documentError}`}
          </span>
        </div>
      )}

      {/* --- MAIN CONTENT --- */}
      {!selectedProjectId ? (
        // --- PROJECT VIEW ---
        <>
          {/* --- Add New Project Form --- */}
          <section className="mb-10 p-6 bg-slate-800 rounded-xl shadow-2xl">
            <h2 className="text-2xl font-semibold mb-4 text-sky-400">Add New Project</h2>
            {projectError && (
              <div className="mb-4 p-4 bg-red-500/20 text-red-300 border border-red-500 rounded-lg flex items-center">
                <AlertCircle size={20} className="mr-3 text-red-400" /> <span>{projectError}</span>
              </div>
            )}
            <form onSubmit={handleAddProject}>
              <FormInput label="Project Name" name="projectName" value={projectForm.projectName} onChange={handleProjectFormChange} placeholder="Enter project name" required />
              <FormInput label="Client Name" name="clientName" value={projectForm.clientName} onChange={handleProjectFormChange} placeholder="Enter client name" required />
              <FormInput label="Facility Name" name="facilityName" value={projectForm.facilityName} onChange={handleProjectFormChange} placeholder="Enter facility name" required />
              <FormInput label="Study Period" name="studyPeriod" value={projectForm.studyPeriod} onChange={handleProjectFormChange} placeholder="e.g., Jan 2025 - Dec 2025" required />
              <FormSelect label="Facility Type" name="facilityType" value={projectForm.facilityType} onChange={handleProjectFormChange} options={FACILITY_TYPES} required />
              <FormInput label="Utility Providers" name="utilityProviders" value={projectForm.utilityProviders} onChange={handleProjectFormChange} placeholder="Enter utility providers" required />
              <FormSelect label="Project Status" name="projectStatus" value={projectForm.projectStatus} onChange={handleProjectFormChange} options={PROJECT_STATUSES} required />
              <button
                type="submit"
                disabled={isSubmittingProject}
                className="mt-4 w-full px-4 py-2 rounded bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors disabled:opacity-50"
              >
                {isSubmittingProject ? "Creating Project..." : "Create Project"}
              </button>
            </form>
          </section>

          {/* --- Existing Projects List --- */}
          <section className="p-6 bg-slate-800 rounded-xl shadow-2xl">
            <h2 className="text-2xl font-semibold mb-4 text-sky-400">Existing Projects</h2>
            {isLoadingProjects ? (
              <p className="text-slate-400">Loading projects...</p>
            ) : (
              <div>
                {projects.length === 0 ? (
                  <p className="text-slate-400">No projects found. Create a new project above.</p>
                ) : (
                  <ul className="space-y-4">
                    {projects.map(project => (
                      <li key={project.id} className="p-4 bg-slate-700 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold text-sky-400">{project.projectName}</h3>
                        <p className="text-slate-300"><strong>Client:</strong> {project.clientName}</p>
                        <p className="text-slate-300"><strong>Facility:</strong> {project.facilityName}</p>
                        <p className="text-slate-300"><strong>Study Period:</strong> {project.studyPeriod}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="text-xs rounded-full" style={{ backgroundColor: projectStatusColor(project.projectStatus), paddingLeft: 8, paddingRight: 8, paddingTop: 4, paddingBottom: 4 }}>
                            {project.projectStatus}
                          </span>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => {
                              setSelectedProjectId(project.id);
                              setEquipmentError(null);
                              setDocumentError(null);
                              cancelEditEquipment();
                            }}
                            className="flex-1 px-4 py-2 rounded bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors"
                          >
                            Manage Project
                          </button>
                          <button
                            onClick={() => openDeleteModal(project.id, 'project', { projectName: project.projectName })}
                            className="flex-1 px-4 py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors"
                          >
                            Delete Project
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        </>
      ) : (
        // --- SELECTED PROJECT VIEW (Documents, Equipment, Analysis, etc.) ---
        <>
          {/* --- Back Button --- */}
          <button
            onClick={() => {
              setSelectedProjectId(null);
              setEquipmentError(null);
              setDocumentError(null);
              cancelEditEquipment();
            }}
            className="mb-6 flex items-center text-sky-400 hover:text-sky-300 transition-colors"
          >
            <ArrowLeftCircle size={20} className="mr-2" />
            Back to Projects
          </button>
          {/* --- Document Upload Section --- */}
          <section id="document-upload-section" className="mb-10 p-6 bg-slate-800 rounded-xl shadow-2xl">
            <h2 className="text-2xl font-semibold mb-4 text-sky-400">Upload Project Documents</h2>
            {documentError && (
              <div className="mb-4 p-4 bg-red-500/20 text-red-300 border border-red-500 rounded-lg flex items-center">
                <AlertCircle size={20} className="mr-3 text-red-400" /> <span>{documentError}</span>
              </div>
            )}
            <form onSubmit={handleAddDocument}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 mb-1 font-medium">Select File</label>
                  <input
                    type="file"
                    accept={PROCESSABLE_IMAGE_TYPES.join(',') + ',' + PROCESSABLE_TEXT_TYPES.join(',')}
                    onChange={handleFileSelect}
                    className="w-full p-2 rounded bg-slate-600 text-slate-100 border border-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                    id="file-input"
                  />
                </div>
                <div>
                  <FormInput label="Document Name" name="documentName" value={documentName} onChange={(e) => setDocumentName(e.target.value)} placeholder="Enter document name" required />
                </div>
                <div>
                  <FormSelect label="Document Type" name="documentType" value={documentType} onChange={(e) => setDocumentType(e.target.value)} options={DOCUMENT_TYPES} required />
                </div>
              </div>
              <button
                type="submit"
                disabled={isUploadingDocument}
                className="mt-4 w-full px-4 py-2 rounded bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors disabled:opacity-50"
              >
                {isUploadingDocument ? "Uploading Document..." : "Upload Document"}
              </button>
            </form>

            {/* --- Document List --- */}
            <h3 className="text-xl font-semibold mt-8 mb-4 text-sky-400">Uploaded Documents</h3>
            {isLoadingDocuments ? (
              <p className="text-slate-400">Loading documents...</p>
            ) : (
              <div>
                {projectDocuments.length === 0 ? (
                  <p className="text-slate-400">No documents found. Upload a document using the form above.</p>
                ) : (
                  <ul className="space-y-4">
                    {projectDocuments.map(doc => (
                      <li key={doc.id} className="p-4 bg-slate-700 rounded-lg shadow-md">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="text-lg font-semibold text-sky-400">{doc.displayName || doc.originalFileName}</h4>
                            <p className="text-slate-300 text-sm"><strong>Type:</strong> {doc.fileType}</p>
                            <p className="text-slate-300 text-sm"><strong>Size:</strong> {(doc.fileSize / 1024).toFixed(1)} KB</p>
                            <p className="text-slate-300 text-sm"><strong>Uploaded:</strong> {doc.uploadedAt?.toDate().toLocaleString() || "N/A"}</p>
                          </div>
                          <div className="flex-shrink-0">
                            <button
                              onClick={() => handleProcessDocument(doc)}
                              className="px-3 py-1 rounded bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-colors"
                            >
                              {processingDocId === doc.id ? "Processing..." : "Process Document"}
                            </button>
                          </div>
                        </div>
                        {doc.processingStatus && (
                          <div className="mt-2">
                            <span className={`text-xs rounded-full px-3 py-1 ${doc.processingStatus === "Processed" ? "bg-green-500 text-white" : doc.processingStatus === "Error" ? "bg-red-500 text-white" : "bg-yellow-500 text-white"}`}>
                              {doc.processingStatus}
                            </span>
                          </div>
                        )}
                        {doc.extractedText && (
                          <div className="mt-2">
                            <p className="text-slate-200 text-sm"><strong>Extracted Text:</strong></p>
                            <p className="text-slate-300 text-sm whitespace-pre-wrap">{doc.extractedText}</p>
                          </div>
                        )}
                        <div className="flex gap-2 mt-4">
                          <a
                            href={doc.downloadURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 px-4 py-2 rounded bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors text-center"
                          >
                            View Document
                          </a>
                          <button
                            onClick={() => openDeleteModal(doc.id, 'document', { documentName: doc.displayName || doc.originalFileName })}
                            className="flex-1 px-4 py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors"
                          >
                            Delete Document
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>

          {/* --- Equipment Form Section --- */}
          <section id="equipment-form-section" className="mb-10 p-6 bg-slate-800 rounded-xl shadow-2xl">
            <h2 className="text-2xl font-semibold mb-4 text-sky-400">
              {editingEquipmentId ? "Edit Equipment" : "Add New Equipment"}
            </h2>
            {equipmentError && (
              <div className="mb-4 p-4 bg-red-500/20 text-red-300 border border-red-500 rounded-lg flex items-center">
                <AlertCircle size={20} className="mr-3 text-red-400" /> <span>{equipmentError}</span>
              </div>
            )}
            <form onSubmit={handleEquipmentSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FormInput label="Equipment Name" name="equipmentName" value={equipmentForm.equipmentName} onChange={handleEquipmentFormChange} placeholder="Enter equipment name" required />
                </div>
                <div>
                  <FormSelect label="Equipment Type" name="equipmentType" value={equipmentForm.equipmentType} onChange={handleEquipmentFormChange} options={EQUIPMENT_TYPES} required />
                </div>
                <div>
                  <FormInput label="Power Rating (kW)" name="powerRating" value={equipmentForm.powerRating} onChange={handleEquipmentFormChange} placeholder="Enter power rating" required />
                </div>
                <div>
                  <FormInput label="Manufacturer" name="manufacturer" value={equipmentForm.manufacturer} onChange={handleEquipmentFormChange} placeholder="Enter manufacturer" />
                </div>
                <div>
                  <FormInput label="Model Number" name="modelNumber" value={equipmentForm.modelNumber} onChange={handleEquipmentFormChange} placeholder="Enter model number" />
                </div>
                <div>
                  <FormInput label="Serial Number" name="serialNumber" value={equipmentForm.serialNumber} onChange={handleEquipmentFormChange} placeholder="Enter serial number" />
                </div>
                <div>
                  <FormInput label="Voltage" name="voltage" value={equipmentForm.voltage} onChange={handleEquipmentFormChange} placeholder="Enter voltage" />
                </div>
                <div>
                  <FormSelect label="Phase" name="phase" value={equipmentForm.phase} onChange={handleEquipmentFormChange} options={EQUIPMENT_PHASES} />
                </div>
                <div>
                  <FormInput label="Location in Facility" name="locationInFacility" value={equipmentForm.locationInFacility} onChange={handleEquipmentFormChange} placeholder="Enter location in facility" />
                </div>
                <div>
                  <FormInput label="Estimated Operating Hours" name="estimatedOperatingHours" value={equipmentForm.estimatedOperatingHours} onChange={handleEquipmentFormChange} placeholder="Enter estimated operating hours" />
                </div>
                <div>
                  <FormInput label="Load Factor (%)" name="loadFactor" value={equipmentForm.loadFactor} onChange={handleEquipmentFormChange} placeholder="Enter load factor" />
                </div>
                <div>
                  <FormSelect label="Usage Category" name="usageCategory" value={equipmentForm.usageCategory} onChange={handleEquipmentFormChange} options={USAGE_CATEGORIES} required />
                </div>
              </div>
              <button
                type="submit"
                disabled={isSubmittingEquipment}
                className="mt-4 w-full px-4 py-2 rounded bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors disabled:opacity-50"
              >
                {isSubmittingEquipment ? "Saving Equipment..." : editingEquipmentId ? "Update Equipment" : "Add Equipment"}
              </button>
              {editingEquipmentId && (
                <button
                  onClick={cancelEditEquipment}
                  type="button"
                  className="mt-2 w-full px-4 py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors"
                >
                  Cancel Edit
                </button>
              )}
            </form>
          </section>

          {/* --- Equipment Inventory Section --- */}
          <section className="p-6 bg-slate-800 rounded-xl shadow-2xl">
            <h2 className="text-2xl font-semibold mb-4 text-sky-400">Equipment Inventory</h2>
            {isLoadingEquipment ? (
              <p className="text-slate-400">Loading equipment...</p>
            ) : (
              <div>
                {equipmentList.length === 0 ? (
                  <p className="text-slate-400">No equipment found. Add new equipment using the form above.</p>
                ) : (
                  <table className="w-full table-auto bg-slate-700 rounded-lg">
                    <thead>
                      <tr className="text-slate-300">
                        <th className="px-4 py-2 text-left">Name/ID</th>
                        <th className="px-4 py-2 text-left">Type</th>
                        <th className="px-4 py-2 text-left">Power (kW)</th>
                        <th className="px-4 py-2 text-left">Usage Category</th>
                        <th className="px-4 py-2 text-left">Est. Hours</th>
                        <th className="px-4 py-2 text-left">Load Factor (%)</th>
                        <th className="px-4 py-2 text-left">Est. Consumption (kWh)</th>
                        <th className="px-4 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipmentList.map(equip => (
                        <tr key={equip.id} className="text-slate-200 hover:bg-slate-600 transition-colors">
                          <td className="px-4 py-2 border-b border-slate-600">{equip.equipmentName}</td>
                          <td className="px-4 py-2 border-b border-slate-600">{equip.equipmentType}</td>
                          <td className="px-4 py-2 border-b border-slate-600">{equip.powerRating}</td>
                          <td className="px-4 py-2 border-b border-slate-600">
                            {USAGE_CATEGORIES.find(cat => cat.id === equip.usageCategory)?.label || equip.usageCategory}
                          </td>
                          <td className="px-4 py-2 border-b border-slate-600">{equip.estimatedOperatingHours}</td>
                          <td className="px-4 py-2 border-b border-slate-600">{equip.loadFactor}</td>
                          <td className="px-4 py-2 border-b border-slate-600">
                            {calculateIndividualEquipmentConsumption(
                              equip,
                              getStudyDurationMonths(selectedProject)
                            ).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                          </td>
                          <td className="px-4 py-2 border-b border-slate-600">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditEquipment(equip)}
                                className="px-3 py-1 rounded bg-yellow-500 text-white text-sm font-semibold hover:bg-yellow-400 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => openDeleteModal(equip.id, 'equipment', { equipmentName: equip.equipmentName })}
                                className="px-3 py-1 rounded bg-red-600 text-white text-sm font-semibold hover:bg-red-500 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </section>

          {/* --- Study Analysis Section --- */}
          <section className="mb-10 p-6 bg-slate-800 rounded-xl shadow-2xl">
            <h2 className="text-2xl font-semibold mb-4 text-sky-400">Study Analysis</h2>
            <button
              onClick={handleRunAnalysis}
              disabled={analysisLoading}
              className="mb-4 px-4 py-2 rounded bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors disabled:opacity-50"
            >
              {analysisLoading ? "Running Analysis..." : "Run Predominant Use Analysis"}
            </button>
            {analysisError && (
              <div className="mb-4 p-4 bg-red-500/20 text-red-300 border border-red-500 rounded-lg flex items-center">
                <AlertCircle size={20} className="mr-3 text-red-400" /> <span>{analysisError}</span>
              </div>
            )}
            {analysisResults && (
              <div className="p-4 bg-green-500/10 border-l-4 border-green-500 rounded-lg">
                <p className="text-green-800 font-semibold">
                  Analysis Complete! Predominant Use Test:{" "}
                  <strong>{analysisResults.isPredominantlyExempt ? "MET" : "NOT MET"}</strong>
                </p>
                <p className="text-green-800">
                  Total Exempt %:{" "}
                  <strong>{analysisResults.exemptPercentage?.toFixed(1)}%</strong>
                </p>
              </div>
            )}

            {/* --- Report Preview --- */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-2 text-sky-400">Report Preview</h3>
              <div className="p-4 bg-slate-700 rounded-lg">
                <ReportPreview project={selectedProject} analysisResults={analysisResults} equipmentList={equipmentList} />
              </div>
              <button
                onClick={handleGenerateReport}
                className="mt-4 px-4 py-2 rounded bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors"
              >
                Download Report as PDF
              </button>
            </div>
          </section>
        </>
      )}

      {/* --- MODALS, FOOTER, ETC. --- */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg shadow-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4 text-sky-400">Confirm Deletion</h3>
            <p className="text-slate-300 mb-4">
              Are you sure you want to delete this {itemToDelete.type}?
              <br />
              <strong>{itemToDelete.type === 'project' ? itemToDelete.projectName : itemToDelete.equipmentName || itemToDelete.documentName}</strong>
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 rounded bg-slate-600 text-slate-100 font-semibold hover:bg-slate-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteItem}
                className="px-4 py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="mt-12 text-center text-sm text-slate-500">
        <p>&copy; {new Date().getFullYear()} Predominant Use AI. Conceptual App.</p>
      </footer>
    </div>
  );
}

// --- Helper: Project Status Color ---
function projectStatusColor(status) {
  switch (status) {
    case "Planning": return "#38bdf8";
    case "Data Collection": return "#818cf8";
    case "Analysis": return "#facc15";
    case "Review": return "#fbbf24";
    case "Completed": return "#22c55e";
    case "Archived": return "#64748b";
    default: return "#38bdf8";
  }
}

export default App;